import Vue, { CreateElement } from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import ClickOutside from 'vue-click-outside'

import { Button } from '@src/elements/button/button'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { snakecaseKeys } from '@src/util/functions'
import { Modal } from '@src/elements/modal/modal'

import './manage-categories.sass'
import Category, { CategoryLanguages } from '@src/data/category'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { CategoryService } from '@src/service/category'
import { Icon } from '@src/elements/icon/icon'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import { AVAILABLE_LOCALES, NotificationTypes } from '@src/util/constants'
import { Input } from '@src/elements/input/input'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import SessionManager from '@src/lib/session_manager'

@Component({
  directives: {
    ClickOutside
  },
  computed: {
    locale () {
      return this.$store.state.i18n.locale || 'en-gb'
    }
  }
})
export default class ManageCategories extends Vue {

  @Prop({ required: false })
  protected closeCallback: () => void

  protected apiClient: ApiClient
  protected categories: Category[] = []
  private loading: boolean = true
  private entityToDelete: number
  private showConfirmPopup: boolean = false
  private hasName: boolean = false
  private category: Category = new Category()
  private locale: string
  private initCategoryName = new CategoryLanguages({ 'en-us': '', 'en-gb': '','de-de': '','it-it': '', 'ja-jp': '', 'fr-fr': '', 'fi-fi': '', 'nl-nl': '', 'el-gr': '', 'pt-pt': '', 'es-es': '', 'tr-tr': '', 'ar-arab': '' })
  private categoryName: CategoryLanguages = { ...this.initCategoryName }
  private selectedTab = 'add'

  constructor () {
    super()
    this.apiClient = new ApiClient()
  }
  created () {
    return this.loadCategories()
  }

  loadCategories () {
    return new CategoryService().getCategories(SessionManager.Instance.isMasterClub(), this.locale)
      .then(categories => this.categories = categories)
      .finally(() => this.loading = false)
  }

  resetForm () {
    this.selectedTab = 'add'
    this.categoryName = { ...this.initCategoryName }
    this.category = new Category()
    return this.loadCategories()
  }

  async saveCategory () {
    this.category.categoryName = this.categoryName
    if (this.category.id) {
      await this.apiClient.put(`${API.TRAINERS_API.CATEGORIES}/${this.category.id}`, { category: snakecaseKeys(this.category) })
        .catch((error) => console.log('cannot update category ', error))
    } else {
      await this.apiClient.post(API.TRAINERS_API.CATEGORIES, { category: snakecaseKeys(this.category) })
        .catch((error) => console.log('cannot save category ', error))
    }
    await this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.SAVE_SUCCESS'), fadeOut: true })
    await this.resetForm()
  }

  showRemoveConfirm (id: any) {
    this.entityToDelete = id
    this.showConfirmPopup = true
  }

  removeCategory (confirm: boolean) {
    this.showConfirmPopup = false
    if (confirm !== true) return undefined
    return this.apiClient.delete(`${API.TRAINERS_API.CATEGORIES}/${this.entityToDelete}`)
        .then(() => {
          this.showConfirmPopup = false
          this.$store.dispatch('showToast', { message: this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.DELETE_SUCCESS'), fadeOut: true })
          return this.resetForm()
        })
        .catch((error) => console.log('cannot remove category ', error.response))
  }

  close () {
    if (this.closeCallback) {
      this.closeCallback()
    }
  }

  renderInputs (h: CreateElement) {
    return AVAILABLE_LOCALES.map(locale => {
      const value = this.categoryName[locale] || ''
      return <div class='input-row'>
        <Tooltip content={this.$t(`LANGUAGES.${locale}`)}>
          <Icon name={`country-flags/${locale}`}/>
        </Tooltip>
        <Input value={value} valueChanged={v => this.changeName(v, locale)}
               class={this.locale === locale && !this.hasName ? 'error' : ''}/>
      </div>
    })
  }

  render (h: CreateElement) {
    this.hasName = (this.categoryName[this.locale] || '') !== ''
    return <Modal show={true} title={this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.MANAGE_CATEGORIES')} closeCallback={this.close} cssClass={'manage-categories'} overflow='scrollable'>

      <div class='category-modal-content'>
        <div class='add-category'>
          <div class={'tabs'}>
            <div
              class={'tab add' + (this.selectedTab === 'add' ? ' selected-tab' : '')}
              onClick={() => this.changeTab()}><p class={'label'}>{this.$t('ADD')}</p></div>
            <div class={'tab edit' + (this.selectedTab === 'edit' ? ' selected-tab' : '')}><p class={'label'}>{this.$t('EDIT')}</p></div>
          </div>
          {this.renderInputs(h)}
          <Button class='addBtn' type='elevated' label={this.$t('SAVE')} click={this.saveCategory} disabled={!this.hasName}/>
        </div>
        <div class='category-list'>
          { this.loading ? <Loader/> : this.categories.map((category: Category) =>
            <div class={'category-card'} onClick={() => this.editCategory(category)}>
              {SessionManager.Instance.isMasterClub() ? <Icon name='workout/certified' /> : ''}
              <Subtitle1 >{category.getName(this.locale)}</Subtitle1>
              <div class={'options'}>
                <Icon name='util/pencil' click={() => this.editCategory(category)} />
                <Icon name='util/minus' click={() => this.showRemoveConfirm(category.id)} />
              </div>
            </div>
          )}
        {this.showConfirmPopup ?
          <ConfirmModal show={true}
                        closeCallback={this.removeCategory}
                        message={this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.CONFIRM_DELETE')}
                        type={NotificationTypes.CONFIRM} /> : null}
          </div>
      </div>
    </Modal>
  }
  private changeName (value, locale) {
    this.categoryName[locale] = value
    this.hasName = this.categoryName[this.locale] !== ''
  }

  private editCategory (category: Category) {
    this.selectedTab = 'edit'
    this.category = category
    this.categoryName = { ...this.category.categoryName }
  }

  private changeTab () {
    this.selectedTab = 'add'
    this.categoryName = { ...this.initCategoryName }
    this.category = new Category()
  }
}
